/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import {
  CL_REGIONS_BASE_URL,
  COMMON_DATA_BASE_URL,
  GEO_DATA_BASE_URL,
  INDUSTRY_FOCUS_BASE_URL,
  PROGRAM_LANGUAGE_BASE_URL,
  SITECONTENT_ACTIVITY_EVENT_TYPES_BASE_URL,
  SITECONTENT_AWARD_KIT_SKU_BASE_URL,
  SITECONTENT_AWARD_PERIODS_BASE_URL,
  SITECONTENT_CLREGIONS_BASE_URL,
  SITECONTENT_COUNTRYREGIONS_BASE_URL,
  SITECONTENT_SCHOOLS_BASE_URL,
  SITECONTENT_SOCIALNETWORKS_BASE_URL,
  SITECONTENT_STATEPROVINCES_BASE_URL,
  TECHNICAL_EXPERTISE_BASE_URL,
  TECHNOLOGY_AREA_BASE_URL,
} from "../Constants/SagaConstants";
import { IApiResult2 } from "../Utility/IApiResult";
import { get, IHttpResponse } from "./ApiUtils";
import {
  IGetClRegionsDataResult,
  IGetCountryDataResult,
  IGetIndustryFocusDataResult,
  IGetRegionDataResult,
  IGetSchoolDataResult,
  IGetSocialNetworkDataResult,
  IGetStateProvinceDataResult,
  IGetTechnicalExpertiseDataResult,
  IGetAwardKitSkuResult,
} from "./Interfaces/SiteContent";
import { IGetActivityEventTypesResult } from "./Interfaces/SiteContent/IGetActivityEventTypesResult";
import { IGetCommonDataResult } from "./Interfaces/SiteContent/IGetCommonDataResult";
import { IGetProgramLanguageDataResult } from "./Interfaces/SiteContent/IGetProgramLanguageDataResult";
import { IGetTechAreaResult } from "./Interfaces/SiteContent/IGetTechAreaResult";

/**
 * fetches Award Periods.
 * NOTE: Errors needs to be caught and handled by the caller as since  this is just a util, there is no telemetry instance.
 * @returns {any} the IGetCountryDataResult Promise.
 */
export async function fetchAwardPeriodList(
  tenant: string
): Promise<IHttpResponse<IApiResult2<any>>> {
  return await get<IApiResult2<any>>(
    `${SITECONTENT_AWARD_PERIODS_BASE_URL}?tenantName=${tenant}`,
    true
  );
}

/**
 * Get Award Kit SKU
 * @returns {IGetAwardKitSkuResult} the IGetAwardKitSkuResult Promise.
 */
export async function fetchAwardKitSkuList(): Promise<
  IHttpResponse<IApiResult2<IGetAwardKitSkuResult>>
> {
  return await get<IApiResult2<IGetAwardKitSkuResult>>(
    `${SITECONTENT_AWARD_KIT_SKU_BASE_URL}`,
    true
  );
}

/**
 * fetches Country Regions.
 * NOTE: Errors needs to be caught and handled by the caller as since  this is just a util, there is no telemetry instance.
 * @returns {IGetCountryDataResult} the IGetCountryDataResult Promise.
 */
export async function fetchCountryRegionList(): Promise<
  IHttpResponse<IApiResult2<IGetCountryDataResult>>
> {
  return await get<IApiResult2<IGetCountryDataResult>>(SITECONTENT_COUNTRYREGIONS_BASE_URL, true);
}

/**
 * fetches the school list.
 * NOTE: Errors needs to be caught and handled by the caller as since  this is just a util, there is no telemetry instance.
 * @param {IGetCountryRegion} countryRegionName The IGetCountryRegion Action Type.
 * @returns {void} void.
 */
export async function fetchSchoolList(
  countryRegionName: string
): Promise<IHttpResponse<IApiResult2<IGetSchoolDataResult>>> {
  return await get<IApiResult2<IGetSchoolDataResult>>(
    SITECONTENT_SCHOOLS_BASE_URL + countryRegionName,
    true
  );
}

/**
 * fetches Regions list (i.e., "Asia", "Europe", etc.).
 * NOTE: Errors needs to be caught and handled by the caller as since  this is just a util, there is no telemetry instance.
 * @returns {IGetCountryDataResult} the IGetCountryDataResult Promise.
 */
export async function fetchRegionList(): Promise<IHttpResponse<IApiResult2<IGetRegionDataResult>>> {
  return await get<IApiResult2<IGetRegionDataResult>>(SITECONTENT_CLREGIONS_BASE_URL, true);
}

/**
 * fetches the stateprovinces list.
 * NOTE: Errors needs to be caught and handled by the caller as since  this is just a util, there is no telemetry instance.
 * @param {IGetCountryRegion} countryRegionName The IGetCountryRegion Action Type.
 * @returns {void} void.
 */
export async function fetchStateProvinceList(
  countryRegionName: string
): Promise<IHttpResponse<IApiResult2<IGetStateProvinceDataResult>>> {
  return await get<IApiResult2<IGetStateProvinceDataResult>>(
    SITECONTENT_STATEPROVINCES_BASE_URL + countryRegionName,
    true
  );
}

/**
 * fetches the social network list.
 * NOTE: Errors need to be caught and handled by the caller as since this is just a util, there is no telemetry instance.
 * @returns {IGetSocialNetworkDataResult} the IGetSocialNetworkDataResult Promise.
 */
export async function fetchSocialNetworkList(): Promise<
  IHttpResponse<IApiResult2<IGetSocialNetworkDataResult>>
> {
  return await get<IApiResult2<IGetSocialNetworkDataResult>>(
    SITECONTENT_SOCIALNETWORKS_BASE_URL,
    true
  );
}

/**
 * fetches the industry focus list.
 * NOTE: Errors need to be caught and handled by the caller as since this is just a util, there is no telemetry instance.
 * @returns {IGetIndustryFocusDataResult} the IGetIndustryFocusDataResult Promise.
 */
export async function fetchIndustryFocusList(): Promise<
  IHttpResponse<IApiResult2<IGetIndustryFocusDataResult>>
> {
  return await get<IApiResult2<IGetIndustryFocusDataResult>>(INDUSTRY_FOCUS_BASE_URL, true);
}

/**
 * fetches the technical expertise list.
 * NOTE: Errors need to be caught and handled by the caller as since this is just a util, there is no telemetry instance.
 * @returns {IGetTechnicalExpertiseDataResult} the IGetIndustryFocusDataResult Promise.
 */
export async function fetchTechnicalExpertiseList(): Promise<
  IHttpResponse<IApiResult2<IGetTechnicalExpertiseDataResult>>
> {
  return await get<IApiResult2<IGetTechnicalExpertiseDataResult>>(
    TECHNICAL_EXPERTISE_BASE_URL,
    true
  );
}

/**
 * fetches the program language list.
 * NOTE: Errors need to be caught and handled by the caller as since this is just a util, there is no telemetry instance.
 * @returns {IGetTechnicalExpertiseDataResult} the IGetProgramLanguageDataResult Promise.
 */
export async function fetchProgramLanguageList(): Promise<
  IHttpResponse<IApiResult2<IGetProgramLanguageDataResult>>
> {
  return await get<IApiResult2<IGetProgramLanguageDataResult>>(PROGRAM_LANGUAGE_BASE_URL, true);
}

/**
 * fetches the technology area list.
 * NOTE: Errors need to be caught and handled by the caller as since this is just a util, there is no telemetry instance.
 * @param {string} tenant Name of the tenant
 * @returns {IGetTechAreaResult} the IGetTechAreaResult Promise.
 */
export async function fetchTechnologyAreaList(
  tenant: string
): Promise<IHttpResponse<IApiResult2<IGetTechAreaResult>>> {
  return await get<IApiResult2<IGetTechAreaResult>>(
    `${TECHNOLOGY_AREA_BASE_URL}?tenant=${tenant}`,
    true
  );
}

/**
 * fetches the cl regions list.
 * NOTE: Errors need to be caught and handled by the caller as since this is just a util, there is no telemetry instance.
 * @returns {IGetRegionsDataResult} the IGetIndustryFocusDataResult Promise.
 */
export async function fetchClRegionsList(): Promise<
  IHttpResponse<IApiResult2<IGetClRegionsDataResult>>
> {
  return await get<IApiResult2<IGetClRegionsDataResult>>(CL_REGIONS_BASE_URL, true);
}

/**
 * fetches the geo data list.
 * NOTE: Errors need to be caught and handled by the caller as since this is just a util, there is no telemetry instance.
 * @returns {IGetRegionsDataResult} the IGetIndustryFocusDataResult Promise.
 */
export async function fetchGeoData(): Promise<IHttpResponse<IApiResult2<IGetClRegionsDataResult>>> {
  return await get<IApiResult2<IGetClRegionsDataResult>>(GEO_DATA_BASE_URL, true);
}

export async function fetchActivityEventTypes(
  tenant: string
): Promise<IHttpResponse<IApiResult2<IGetActivityEventTypesResult>>> {
  return await get<IApiResult2<IGetActivityEventTypesResult>>(
    `${SITECONTENT_ACTIVITY_EVENT_TYPES_BASE_URL}?tenant=${tenant}`,
    true
  );
}

/**
 * fetches the common data list.
 * NOTE: Errors need to be caught and handled by the caller as since this is just a util, there is no telemetry instance.
 * @returns {IGetCommonDataResult} the IGetIndustryFocusDataResult Promise.
 */
export async function fetchCommonData(): Promise<IHttpResponse<IApiResult2<IGetCommonDataResult>>> {
  return await get<IApiResult2<IGetCommonDataResult>>(COMMON_DATA_BASE_URL, true);
}
